import { Flag, LoaderCircle, CircleCheck } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import type { ProjectActionProps, ProjectActionState } from '@shared/widget-schemas';
import { cn } from '@/lib/utils';
import { HeroShell, HeroIcon, HeroActionButtons, type WidgetActionProps } from './chat-widget-ui';
import { capitalizeFirst } from './textFormat';
import { SkBar, SkBtn, SkCircle } from './skeletons';

const PURPLE = 'var(--color-purple-400)';

/** Milestone state → accent hue, default icon, and default kicker label. */
const STATE_CONFIG: Record<ProjectActionState, { accent: string; icon: LucideIcon; kicker: string }> = {
  action_required: { accent: 'var(--color-orange-500)', icon: Flag, kicker: 'Action required' },
  in_progress: { accent: 'var(--color-blue-500)', icon: LoaderCircle, kicker: 'In progress' },
  reached: { accent: 'var(--color-green-500)', icon: CircleCheck, kicker: 'Milestone reached' },
};

export function ProjectActionWidget({
  title,
  state = 'action_required',
  kicker,
  body,
  icon,
  progress,
  recap,
  actions,
  onAction,
}: ProjectActionProps & WidgetActionProps) {
  const cfg = STATE_CONFIG[state] ?? STATE_CONFIG.action_required;

  return (
    <HeroShell
      variant="action"
      accent={cfg.accent}
      icon={<HeroIcon name={icon} fallback={cfg.icon} />}
      kicker={kicker ?? cfg.kicker}
      title={title}
      desc={body}
      actions={actions?.length ? <HeroActionButtons actions={actions} onAction={onAction} /> : undefined}
    >
      {progress && <ProgressBar pct={progress.pct} label={progress.label} />}
      {recap && recap.length > 0 && <RecapWell items={recap} />}
    </HeroShell>
  );
}

/** Labelled progress bar (LcProgress). */
function ProgressBar({ pct, label }: { pct: number; label?: string }) {
  return (
    <div className="flex flex-col gap-[7px]">
      <div className="flex items-baseline justify-between font-mono text-[11px] text-muted-foreground">
        <span>{label ? capitalizeFirst(label) : label}</span>
        <span className="font-semibold" style={{ color: PURPLE }}>
          {pct}%
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: 'var(--gradient-primary)' }}
        />
      </div>
    </div>
  );
}

/** Recap stat well (LcRecap container, label/value rows). */
function RecapWell({ items }: { items: { label: string; value: string }[] }) {
  return (
    <div
      className="flex flex-col gap-[9px] rounded-xl border border-border px-4 py-3.5"
      style={{ background: 'var(--tinted-bg)' }}
    >
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between gap-3">
          <span className="text-[12.5px] leading-[1.4] text-muted-foreground">{capitalizeFirst(it.label)}</span>
          <span className="font-mono text-[12.5px] font-semibold text-foreground">{it.value}</span>
        </div>
      ))}
    </div>
  );
}

export function ProjectActionWidgetSkeleton({
  className,
  progress = true,
}: {
  className?: string;
  progress?: boolean;
}) {
  return (
    <div
      className={cn(
        'glass-card flex w-full max-w-[min(560px,100%)] flex-col gap-[18px] px-[26px] py-6',
        className,
      )}
    >
      <div className="flex items-start gap-4">
        <SkCircle size="48px" radius="14px" />
        <div className="flex flex-1 flex-col gap-2">
          <SkBar width="130px" height="9px" radius="9999px" />
          <SkBar width="70%" height="16px" />
          <SkBar width="95%" height="9px" />
          <SkBar width="84%" height="9px" />
        </div>
      </div>
      {progress && (
        <div className="flex flex-col gap-2">
          <SkBar width="100%" height="6px" radius="9999px" />
          <SkBar width="100%" height="56px" radius="12px" />
        </div>
      )}
      <div className="flex justify-end gap-2.5">
        <SkBtn width="120px" />
        <SkBtn width="150px" />
      </div>
    </div>
  );
}
